import apiClient from '../config/axios';

export interface Seller {
  id: number;
  seller_name: string;
  contact_person: string;
  email: string;
  phone: string;
  address?: string;
  username: string;
  is_active: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface SellerRegistrationData {
  seller_name: string;
  contact_person: string;
  email: string;
  phone: string;
  address?: string;
  username?: string;
  password: string;
  is_active?: boolean;
}

export interface SellerLoginData {
  email: string;
  // username: string; // commented out for email-only login
  // password: string; // commented out for email-only login
}

export interface SellerResponse {
  success: boolean;
  message: string;
  data: {
    token: string;
    user: {
      id: number;
      username: string;
      email: string;
      seller_name: string;
      contact_person: string;
      is_main_seller: boolean;
    };
  };
}

export interface SellerDetailResponse {
  status: string;
  data: {
    id: number;
    attributes: Seller;
  };
}

export interface SellerRegistrationResponse {
  status: string;
  message: string;
  data: {
    id: number;
    attributes: Seller;
  };
  token: string;
  expiresIn: string;
}

export interface SellersListResponse {
  status: string;
  data: Array<{
    id: number;
    attributes: Seller;
  }>;
  meta: {
    pagination: {
      page: number;
      pageSize: number;
      total: number;
      pageCount: number;
    };
  };
}

class SellerService {
  /**
   * Register a new seller
   */
  async registerSeller(sellerData: SellerRegistrationData): Promise<SellerRegistrationResponse> {
    const response = await apiClient.post('/sellers', sellerData);
    return response.data;
  }

  /**
   * Login a seller
   */
  async loginSeller(loginData: SellerLoginData): Promise<SellerResponse> {
    const response = await apiClient.post('/sellers/login', loginData);
    return response.data;
  }

  /**
   * Get all sellers with optional filtering
   */
  async getSellers(params?: {
    is_active?: boolean;
    search?: string;
    page?: number;
    pageSize?: number;
  }): Promise<SellersListResponse> {
    const response = await apiClient.get('/sellers', { params });
    return response.data;
  }

  /**
   * Get a specific seller by ID
   */
  async getSellerById(sellerId: number): Promise<SellerDetailResponse> {
    const response = await apiClient.get(`/sellers/${sellerId}`);
    return response.data;
  }

  /**
   * Update a seller
   */
  async updateSeller(sellerId: number, updateData: Partial<Seller>): Promise<SellerResponse> {
    const response = await apiClient.put(`/sellers/${sellerId}`, updateData);
    return response.data;
  }

  /**
   * Delete a seller (hard delete)
   */
  async deleteSeller(sellerId: number): Promise<SellerResponse> {
    const response = await apiClient.delete(`/sellers/${sellerId}`);
    return response.data;
  }

  /**
   * Activate a seller
   */
  async activateSeller(sellerId: number): Promise<SellerResponse> {
    const response = await apiClient.patch(`/sellers/${sellerId}/activate`);
    return response.data;
  }

  /**
   * Deactivate a seller (soft delete)
   */
  async deactivateSeller(sellerId: number): Promise<SellerResponse> {
    const response = await apiClient.patch(`/sellers/${sellerId}/deactivate`);
    return response.data;
  }

  /**
   * Get seller profile by username or email
   */
  async getSellerProfile(identifier: string): Promise<SellerDetailResponse> {
    const response = await apiClient.get(`/sellers/profile/${identifier}`);
    return response.data;
  }
}

const sellerService = new SellerService();
export default sellerService;
